import React, { useEffect, useRef, useState, useCallback } from "react";
import MemberLayout from "../../components/MemberLayout";
import { memberApi } from "../../lib/api";
import { useParams } from "react-router-dom";
import { toast } from "../../hooks/use-toast";
import { Pin, ExternalLink, BarChart3, Check, Clock } from "lucide-react";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleString() : ""; } catch { return iso; } };

const NetworkUpdateCard = ({ u, onVoted, isHighlighted, scrollRef }) => {
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const isPoll = u.kind === 'poll';
  const results = u.poll_results;
  const voted = !!(u.my_vote && u.my_vote.length);
  const allowMultiple = isPoll && u.poll?.allow_multiple;
  const closed = isPoll && !u.poll_open;

  const toggle = (id) => {
    if (voted || closed) return;
    if (allowMultiple) setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    else setSelected([id]);
  };

  const submit = async () => {
    if (!selected.length) { toast({ title: "Please select at least one option" }); return; }
    setSubmitting(true);
    try {
      await memberApi.post(`/member/network-updates/${u.id}/vote`, { option_ids: selected });
      toast({ title: "Vote submitted" });
      onVoted && onVoted();
    } catch (e) { toast({ title: "Vote failed", description: e?.response?.data?.detail || "Try again" }); }
    finally { setSubmitting(false); }
  };

  const showResults = !!results;

  return (
    <article
      ref={isHighlighted ? scrollRef : undefined}
      className={`rounded-lg p-6 mb-4 ${isHighlighted ? 'ring-2 ring-[#e0684d]' : ''}`}
      style={{ background: '#fbf8ef' }}
      data-testid={`nu-card-${u.id}`}
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap text-[12px] text-[#0d2a20]/60">
        {u.pinned && <span className="px-2 py-1 rounded font-medium" style={{ background: '#fde5c7', color: '#8a4a10' }}><Pin size={11} className="inline mr-1" />Pinned</span>}
        {isPoll && <span className="px-2 py-1 rounded font-medium" style={{ background: '#e8dcc4', color: '#5a4a1a' }}><BarChart3 size={11} className="inline mr-1" />Poll {closed ? '· closed' : ''}</span>}
        <span>{fmtDate(u.published_at || u.created_at)}</span>
      </div>
      <h2 className="hero-title text-[24px] leading-tight mb-2">{u.title}</h2>
      {u.image_url && (
        <div className="mb-4 rounded-md overflow-hidden">
          <img src={u.image_url.startsWith("http") ? u.image_url : `${backendUrl}${u.image_url}`} alt="" className="w-full max-h-[360px] object-cover" />
        </div>
      )}
      {u.body && <div className="prose max-w-none text-[15px] leading-relaxed" dangerouslySetInnerHTML={{ __html: u.body }} />}
      {u.link_url && (
        <a href={u.link_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-[13px] link-underline" data-testid={`nu-card-link-${u.id}`}>
          <ExternalLink size={13} /> {u.link_text || u.link_url}
        </a>
      )}
      {isPoll && (
        <div className="mt-4 rounded-md p-4" style={{ background: '#f4efe4' }}>
          <div className="text-[15px] font-medium mb-3">{u.poll?.question}</div>
          {!voted && !closed ? (
            <>
              <div className="space-y-2">
                {(u.poll?.options || []).map(opt => (
                  <label key={opt.id} className="flex items-start gap-2 p-3 rounded-md cursor-pointer bg-white border" style={{ borderColor: selected.includes(opt.id) ? '#e0684d' : 'rgba(13,42,32,0.1)' }} data-testid={`nu-poll-opt-${u.id}-${opt.id}`}>
                    <input type={allowMultiple ? 'checkbox' : 'radio'} name={`poll-${u.id}`} checked={selected.includes(opt.id)} onChange={() => toggle(opt.id)} className="mt-1 accent-[#e0684d]" />
                    <span className="text-[14px]">{opt.text}</span>
                  </label>
                ))}
              </div>
              <button onClick={submit} disabled={submitting || !selected.length} data-testid={`nu-poll-vote-${u.id}`} className="mt-3 px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60">{submitting ? "Voting…" : "Cast vote"}</button>
              {u.poll?.hide_results_until_close && <div className="mt-2 text-[12px] text-[#0d2a20]/60 flex items-center gap-1"><Clock size={11} /> Results will be visible when the poll closes.</div>}
            </>
          ) : showResults ? (
            <div className="space-y-2">
              <div className="text-[12px] text-[#0d2a20]/60 mb-2">{results.total_voters} voter{results.total_voters === 1 ? '' : 's'}{voted && <> · <Check size={11} className="inline" /> Your vote is recorded</>}</div>
              {results.options.map(opt => {
                const mine = u.my_vote?.includes(opt.id);
                return (
                  <div key={opt.id}>
                    <div className="flex items-center justify-between text-[13px] mb-1">
                      <span className={mine ? "font-medium" : ""}>{opt.text}{mine && " (your vote)"}</span>
                      <span className="text-[#0d2a20]/60">{opt.count} · {opt.percent}%</span>
                    </div>
                    <div className="h-2 rounded-full" style={{ background: '#ece5d3' }}>
                      <div className="h-2 rounded-full" style={{ width: `${opt.percent}%`, background: mine ? '#e0684d' : '#0f3d2e' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : voted ? (
            <div className="text-[13px] text-[#0d2a20]/70 flex items-center gap-2"><Check size={13} /> Your vote is recorded. Results will be visible when the poll closes.</div>
          ) : (
            <div className="text-[13px] text-[#0d2a20]/60 flex items-center gap-1"><Clock size={12} /> Results will be published once this poll closes.</div>
          )}
        </div>
      )}
    </article>
  );
};

const MemberNetworkUpdates = () => {
  const { id: highlightedId } = useParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const highlightRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await memberApi.get("/member/network-updates");
      setList(data);
    } catch { toast({ title: "Failed to load updates" }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!loading && highlightedId && highlightRef.current) {
      // Small delay so layout settles before scrolling
      const t = setTimeout(() => {
        try { highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {}
      }, 120);
      return () => clearTimeout(t);
    }
  }, [loading, highlightedId, list]);

  return (
    <MemberLayout>
      <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Members</div>
      <h1 className="hero-title text-[40px] md:text-[56px] leading-none mb-6">Network <span className="font-serif-i text-[#e0684d]">Updates</span></h1>
      {loading ? <div className="p-10 text-center text-[14px] text-[#0d2a20]/60">Loading…</div> :
        list.length === 0 ? <div className="p-10 text-center text-[14px] text-[#0d2a20]/60 rounded-lg" style={{ background: '#fbf8ef' }}>No updates yet — check back soon.</div> :
        list.map(u => (
          <NetworkUpdateCard
            key={u.id}
            u={u}
            onVoted={load}
            isHighlighted={u.id === highlightedId}
            scrollRef={highlightRef}
          />
        ))
      }
    </MemberLayout>
  );
};

export default MemberNetworkUpdates;
